import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Minus, Plus, ShoppingBag, Trash2, X } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { inr } from "@/lib/api";
import { EASE } from "@/components/Reveal";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeFromCart, subtotal, count } = useShop();
  const navigate = useNavigate();

  const goCheckout = () => {
    setCartOpen(false);
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-[90] bg-[#1C1712]/50 backdrop-blur-sm"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
            data-testid="cart-overlay"
          />
          <motion.aside
            className="fixed right-0 top-0 z-[95] h-full w-full sm:w-[430px] bg-surface border-l border-gold/25 flex flex-col"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: EASE }}
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-6 py-5 border-b border-line">
              <div>
                <p className="overline-tag">Your Selection</p>
                <h3 className="font-display text-2xl font-semibold mt-1">Cart · {count} {count === 1 ? "piece" : "pieces"}</h3>
              </div>
              <button onClick={() => setCartOpen(false)} className="h-10 w-10 grid place-items-center text-ink2 hover:text-gold transition-colors" data-testid="cart-close">
                <X size={20} />
              </button>
            </div>

            {cart.length === 0 ? (
              <div className="flex-1 grid place-items-center px-8 text-center">
                <div>
                  <ShoppingBag size={36} className="mx-auto text-gold2" />
                  <p className="mt-4 font-display text-2xl">Your cart awaits its first heirloom</p>
                  <p className="mt-2 text-sm text-ink2">Browse the atelier and add a piece that speaks to you.</p>
                  <button onClick={() => setCartOpen(false)} className="btn-ghost-gold mt-6">Continue Exploring</button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
                  {cart.map((i) => (
                    <div key={i.id} className="flex gap-4" data-testid={`cart-item-${i.id}`}>
                      <div className="h-24 w-20 shrink-0 overflow-hidden bg-bg2 border border-line">
                        {i.image ? (
                          <img src={i.image} alt={i.name} className="h-full w-full object-cover" />
                        ) : (
                          <div className="h-full w-full grid place-items-center font-display text-ink3">SJ</div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-2">
                          <p className="font-display text-lg leading-tight truncate">{i.name}</p>
                          <button onClick={() => removeFromCart(i.id)} className="text-ink3 hover:text-red-600 transition-colors" data-testid={`cart-remove-${i.id}`}>
                            <Trash2 size={15} />
                          </button>
                        </div>
                        <p className="font-mono text-[10px] text-ink3 mt-0.5 uppercase">{i.category}</p>
                        <div className="mt-2.5 flex items-center justify-between">
                          <div className="flex items-center border border-line">
                            <button onClick={() => updateQty(i.id, i.qty - 1)} className="h-8 w-8 grid place-items-center text-ink2 hover:text-gold" data-testid={`cart-qty-minus-${i.id}`}><Minus size={12} /></button>
                            <span className="w-8 text-center font-mono text-sm" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                            <button onClick={() => updateQty(i.id, i.qty + 1)} className="h-8 w-8 grid place-items-center text-ink2 hover:text-gold" data-testid={`cart-qty-plus-${i.id}`}><Plus size={12} /></button>
                          </div>
                          <span className="font-mono text-sm text-ink">{inr(i.price * i.qty)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-line px-6 py-5 space-y-4">
                  <div className="flex justify-between items-baseline">
                    <span className="font-cinzel text-[11px] uppercase text-ink2" style={{ letterSpacing: "0.2em" }}>Subtotal</span>
                    <span className="font-mono text-xl text-ink" data-testid="cart-subtotal">{inr(subtotal)}</span>
                  </div>
                  <p className="text-[11px] text-ink3 font-mono">Insured shipping on us · Care card included with every order</p>
                  <button onClick={goCheckout} className="btn-gold w-full" data-testid="cart-checkout-button">Proceed to Checkout</button>
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
